import React, { useState, useEffect, useCallback, useRef } from 'react';

import classNames from 'classnames';

import { UiBuilding, UiExclamationTriangle } from '@vyaguta/vyaguta-icons/ui';

import CalendarCross from 'assets/icon/CalendarCross';

import { COLORS } from 'constants/color';
import { WORK_LOCATION } from 'constants/worklog';
import { attendanceActionMapper } from 'constants/workStatus';
import { attendanceIconMapper } from 'constants/attendanceStatus';

import { fetchUserWorkStatus } from 'services/user';

import { getFormattedDate } from 'utils/date';
import { handleError } from 'utils/errorHandler';

import RemoteIcon from 'components/common/remoteIcon';
import AttendanceWifiLog from 'components/common/attendance/AttendanceWifiLog';
import { WorklogItem, WorkStatusSummary } from 'interface/leave';
import { useSelfAttendance } from 'contexts/SelfAttendanceContext';

interface WorkStatusItem extends WorkStatusSummary {
  holiday?: boolean;
  hasCheckedIn?: boolean;
  worklog?: WorklogItem | null;
}

interface WorkStatusProps {
  id: number | string;
  userId: number | string;
  links: Array<{ label: string; to: string }>;
}

/**
 * get attendance Icon and label
 * 1. on leave
 * 2. wfh+wifi log
 * 3. wfh+no wifi log
 * 4. wfo+wifi log
 * 5. wfo+no wifi log
 * 6. pending + wifi-log
 * 7. pending no wifi log
 * 8. no wifi log
 */
const getIconAndClassName = (workStatus: WorkStatusItem) => {
  if (workStatus.leave && !workStatus.holiday) {
    return {
      Icon: CalendarCross,
      className: attendanceIconMapper.LEAVE,
      label: attendanceActionMapper.LEAVE,
      color: COLORS.purple,
      hasLogo: false,
    };
  }

  if (workStatus.worklog) {
    const { location } = workStatus.worklog;

    if (location === WORK_LOCATION.HOME) {
      if (workStatus.hasCheckedIn) {
        return {
          Icon: RemoteIcon,
          className: attendanceIconMapper.WIFI_CONNECTED,
          label: attendanceActionMapper.WFH_WIFI,
          hasLogo: true,
        };
      }

      return {
        Icon: RemoteIcon,
        className: attendanceIconMapper.WIFI_CONNECTED,
        label: attendanceActionMapper.WFH,
        hasLogo: false,
      };
    }

    if (workStatus.hasCheckedIn) {
      return {
        Icon: UiBuilding,
        className: attendanceIconMapper.WIFI_CONNECTED,
        label: attendanceActionMapper.WFO_WIFI,
        hasLogo: true,
      };
    }

    return {
      Icon: UiBuilding,
      className: attendanceIconMapper.WIFI_NOT_CONNECTED,
      label: attendanceActionMapper.WFO_NOWIFI,
      hasLogo: true,
      logoColor: COLORS.grey20,
    };
  }

  if (workStatus.hasCheckedIn && !workStatus.holiday) {
    return {
      Icon: UiExclamationTriangle,
      className: attendanceIconMapper.PENDING,
      label: attendanceActionMapper.Pending_WIFI,
      color: COLORS.orange80,
      hasLogo: true,
    };
  }

  if (!workStatus.holiday) {
    return {
      Icon: UiExclamationTriangle,
      className: attendanceIconMapper.PENDING,
      label: attendanceActionMapper.PENDING_NOWIFI,
      color: COLORS.orange80,
      hasLogo: false,
    };
  }

  return { Icon: null };
};

const WorkStatus: React.FC<WorkStatusProps> = (props) => {
  const { id, userId, links } = props;
  const [workStatus, setWorkStatus] = useState<WorkStatusItem | null>(null);
  const previousIdRef = useRef<number | string | null>(null);

  const {
    selfAttendance: { missing: selfMissing, pending: selfPending },
  } = useSelfAttendance();

  const getWorkStatusInfo = useCallback(async () => {
    try {
      const params = {
        date: getFormattedDate(),
        userIds: id,
        withWifiLog: true,
      };

      const data = await fetchUserWorkStatus(params);

      setWorkStatus(data[0]);
    } catch (e) {
      handleError(e);
    }
  }, [id]);

  useEffect(() => {
    const previousId = previousIdRef.current;
    const isFirstRender = !previousId;
    const isIdChanged = !!previousId && previousId !== id;
    const isAttendanceUpdated = !!(userId === id && (selfMissing || selfPending));

    if (isFirstRender || isIdChanged || isAttendanceUpdated) {
      previousIdRef.current = id;

      getWorkStatusInfo();
    }
  }, [id, selfMissing, selfPending, userId, getWorkStatusInfo]);

  if (!workStatus) {
    return;
  }

  const { Icon, label, color, className, hasLogo, logoColor } = getIconAndClassName(workStatus);

  if (!Icon) {
    return;
  }

  return (
    <div
      className={classNames('status-height work-title d-flex align-items-center justify-content-end gap-px-8', {
        'status-margin ': !links.length,
      })}
    >
      <AttendanceWifiLog
        Icon={Icon}
        color={color || COLORS.white}
        className={className}
        hasLogo={hasLogo}
        logoColor={logoColor}
      />
      <h3 className="font-12"> {label}</h3>
    </div>
  );
};

export default WorkStatus;
